// The emails the server will send, by touch: each stays editable while it is a draft (subject, body, brochures for
// the first), the sender can be switched, Personalise re-greets and re-signs, and touch 1 is approved from here.
import { Loader2, Play, Save, Send, Trash2, Wand2 } from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
} from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import type { CrmDataProvider } from "../../providers/types";
import { useConfigurationContext } from "../../root/ConfigurationContext";
import { personaliseBody } from "../leads/draftNote";
import { DEFAULT_SEND_AS, type Sender } from "../leads/outlook";
import { SenderSelect } from "../leads/SenderSelect";
import { useDefaultSender } from "../leads/useDefaultSender";
import { beforeApproval, followupsStopped } from "./preLeadDisplay";
import { recipientOf } from "./preLeads";
import type { PreLead, PreLeadMessage } from "./types";
import { useDrawerTask } from "./useDrawerTask";

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

const DraftEmail = ({
  preLead,
  message,
  signature,
  onApprove,
}: {
  preLead: PreLead;
  message: PreLeadMessage;
  signature: string;
  onApprove: (row: PreLead) => Promise<void>;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const brochures = useConfigurationContext().lead_brochures ?? [];
  const { running, run } = useDrawerTask();
  const t = (key: string, args?: Record<string, unknown>) =>
    translate(`resources.pre_leads.drawer.${key}`, args);
  const [subject, setSubject] = useState(message.subject ?? "");
  const [body, setBody] = useState(message.body ?? "");
  const [attached, setAttached] = useState(message.attached ?? []);
  const editable = message.status === "draft";
  const first = message.touch === 1;
  const dirty =
    subject !== (message.subject ?? "") ||
    body !== (message.body ?? "") ||
    attached.join("\n") !== (message.attached ?? []).join("\n");

  const save = async () => {
    await dataProvider.update("pre_lead_messages", {
      id: message.id,
      // a person's edit: ThnkOS no longer rewrites it, and the draft's flag is answered
      data: { subject, body, attached, draft_by: "user", error: null },
      previousData: message,
    });
    // the server attaches the row's brochures to the first email
    if (first)
      await dataProvider.update("pre_leads", {
        id: preLead.id,
        data: { brochures: attached },
        previousData: preLead,
      });
  };

  const date = message.sent_at
    ? t("sent", { date: when(message.sent_at) })
    : message.scheduled_for
      ? t("sends", { date: when(message.scheduled_for) })
      : null;

  return (
    <section className="flex flex-col gap-2 rounded-md border p-3">
      <header className="flex items-center justify-between gap-2 text-sm">
        <span className="font-semibold">
          {t("touch", { touch: message.touch ?? 1 })} ·{" "}
          {t(`message_status.${message.status}`)}
        </span>
        {date && <span className="text-xs text-muted-foreground">{date}</span>}
      </header>
      {editable ? (
        <>
          <Input
            aria-label={t("subject")}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <Textarea
            aria-label={t("body")}
            className="min-h-48"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          {message.error && (
            <p className="text-sm text-destructive">{message.error}</p>
          )}
          {first && brochures.length > 0 && (
            <fieldset className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <legend className="sr-only">{t("attach")}</legend>
              <span className="text-muted-foreground">{t("attach")}</span>
              {brochures.map((b) => (
                <label key={b.name} className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={attached.includes(b.name)}
                    onChange={(e) =>
                      setAttached(
                        e.target.checked
                          ? [...attached, b.name]
                          : attached.filter((n) => n !== b.name),
                      )
                    }
                  />
                  {b.name}
                </label>
              ))}
            </fieldset>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setBody(personaliseBody(body, recipientOf(preLead), signature))
              }
            >
              <Wand2 />
              {t("personalise")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!dirty || running != null}
              onClick={() =>
                run("save", async () => {
                  await save();
                  notify("resources.pre_leads.drawer.notify.saved");
                })
              }
            >
              {running === "save" ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Save />
              )}
              {translate("ra.action.save")}
            </Button>
            {!first && (
              <Button
                variant="ghost"
                size="sm"
                disabled={running != null}
                onClick={() =>
                  run("delete", () =>
                    dataProvider.delete("pre_lead_messages", {
                      id: message.id,
                      previousData: message,
                    }),
                  )
                }
              >
                <Trash2 />
                {t("delete_followup")}
              </Button>
            )}
            {first && preLead.status === "ready" && (
              <Button
                size="sm"
                className="ml-auto"
                disabled={running != null}
                onClick={() =>
                  run("approve", async () => {
                    if (dirty) await save();
                    await onApprove(preLead);
                  })
                }
              >
                {running === "approve" ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <Send />
                )}
                {translate("resources.pre_leads.bulk.approve")}
              </Button>
            )}
          </div>
        </>
      ) : (
        <div className="text-sm">
          <p className="font-medium">{message.subject}</p>
          <p className="whitespace-pre-wrap text-muted-foreground">
            {message.body}
          </p>
          {message.error && (
            <p className="mt-1 text-destructive">{message.error}</p>
          )}
        </div>
      )}
    </section>
  );
};

export const DraftTab = ({
  preLead,
  messages,
  signature,
  onApprove,
}: {
  preLead: PreLead;
  messages: PreLeadMessage[];
  signature: string;
  onApprove: (row: PreLead) => Promise<void>;
}) => {
  const translate = useTranslate();
  const config = useConfigurationContext();
  const defaultSender = useDefaultSender();
  const { identity } = useGetIdentity();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const { running, run } = useDrawerTask();
  const open = beforeApproval(preLead);
  const outgoing = messages
    .filter((m) => m.direction === "out")
    .sort((a, b) => (a.touch ?? 0) - (b.touch ?? 0) || a.id - b.id);

  const setSender = (sender: Sender) =>
    run("sender", () =>
      dataProvider.update<PreLead>("pre_leads", {
        id: preLead.id,
        data: {
          sender,
          mailbox:
            sender === "personal"
              ? String(identity?.email ?? "")
              : config.outlook_send_as || DEFAULT_SEND_AS,
        },
        previousData: preLead,
      }),
    );

  return (
    <div className="flex flex-col gap-3">
      <SenderSelect
        value={preLead.sender ?? defaultSender}
        onChange={setSender}
        disabled={!open}
        title={
          open ? undefined : translate("resources.pre_leads.drawer.locked")
        }
      />
      {followupsStopped(preLead) && (
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          disabled={running != null}
          onClick={() =>
            run("resume", async () => {
              const { next_touch_at } = await dataProvider.outreach<{
                next_touch_at: string;
              }>({ action: "resume_followups", pre_lead_id: preLead.id });
              notify("resources.pre_leads.drawer.notify.resumed", {
                messageArgs: { date: when(next_touch_at) },
              });
            })
          }
        >
          {running === "resume" ? (
            <Loader2 className="animate-spin" />
          ) : (
            <Play />
          )}
          {translate("resources.pre_leads.drawer.resume")}
        </Button>
      )}
      {outgoing.length ? (
        outgoing.map((m) => (
          <DraftEmail
            // a rewrite on the server (Keep again, Write to this person) restarts the editor
            key={`${m.id}:${m.to_email}:${m.subject}:${m.body}`}
            preLead={preLead}
            message={m}
            signature={signature}
            onApprove={onApprove}
          />
        ))
      ) : (
        <p className="text-sm text-muted-foreground">
          {translate("resources.pre_leads.drawer.no_draft")}
        </p>
      )}
    </div>
  );
};
